/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-05 09:29:22                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 10:11:49                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * Styles
  * Quotations: Quotations list page
    * useEffect: Get quotation list and set page number
    * useEffect: Paginate data
    * handleQuotationCreate: handle on submit event, create quotation
*/

/* Imports */
import { useEffect, useState, useCallback, useMemo } from "react";
import { t } from "i18next";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
/***/

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";
import {
  SearchOptions,
  SearchQuery,
  searchWithFilters,
  toSearchOrder,
} from "../../services/api/search";
/***/

/* Components */
import { Pagination } from "../../components/pagination/Pagination";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import QuotationsTable from "./sections/QuotationsTable";
import QuotationForm, { QuotationFormValidator } from "../../components/data/quotation/QuotationForm";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import QuotationFilterForm, { getQueriesFromFilter } from "./filter/QuotationFilterForm";
/***/

/* Styles */
import "./Quotations.scss";
import { quotationExportDescriptor } from "../../services/export/data/quotationExport";
import ExportButton from "../../components/export-button/ExportButton";
import { useQuotationsFilters } from "../../hooks/filters/useQuotationsFilters";
import { useTablePageSize } from "../../hooks/useTablePageSize";
/***/

/**
 * Composant principal pour la gestion des devis
 * Gère l'affichage, le filtrage, la pagination et l'export des devis
 */
export function Quotations() {
  const [quotationsPageState, setQuotationsPageState] = useState<EasyTech.Quotation[]>([]);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "date", orderDesc: true });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
  };

  // Fonction pour réinitialiser la page
  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  // Utilisation du hook de filtres
  const {
    filters,
    handleFilterSubmit,
    clearFilter: handleFilterClear,
    clearAllFilters,
    displayFilters
  } = useQuotationsFilters(resetPage);
  /**
   * Charge les devis filtrés depuis l'API
   * Gère les différents types de filtres (référence, client, date, technicien)
   * 
   * Cette fonction construit une requête de recherche avec les différents critères
   * et utilise l'API endpoint /users/quotations/search pour obtenir les résultats.
   * En l'absence de filtres, elle utilise la pagination locale.
   */
  /** Le tri affiche. Une seule definition, pour que l'export ne puisse pas diverger de la table. */
  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  const loadQuotationsWithFilters = useCallback(async () => {
    if (!filters) return;
    setIsLoading(true);
    try {
      // Construction des critères de recherche en fonction des filtres actifs
      const queries: SearchQuery[] = getQueriesFromFilter(filters);

      // Configuration des options de recherche (pagination, tri, filtres)
      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries,
      };

      try {
        // Appel à l'API de recherche
        const response = await searchWithFilters<EasyTech.Quotation>(
          "quotations",
          searchOptions
        );

        // Traitement des résultats
        if (response && response.data && Array.isArray(response.data)) {
          setQuotationsPageState(response.data);
          setPageNumberState(response.totalPages);
        } else {
          // Gestion du cas où la réponse est invalide
          setQuotationsPageState([]);
          setPageNumberState(1);
        }
      } catch (searchError) {
        // Gestion des erreurs d'API
        setQuotationsPageState([]);
        setPageNumberState(1);
      }
    } catch (error) {
      // Gestion des erreurs générales
      setQuotationsPageState([]);
      setPageNumberState(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize]);

  /**
   * Charge initialement tous les devis depuis l'API
   * Cette données sont utilisées pour la pagination locale
   */
  useEffect(() => {
    loadQuotationsWithFilters();
  }, [loadQuotationsWithFilters]);

  /**
   * Gère la création d'un nouveau devis
   * @param data - Données du nouveau devis
   * @returns Promise<void>
   */
  const handleQuotationCreate = (data: EasyTech.Quotation): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(data, QuotationFormValidator)) {
        api.post("users/quotations", { ...data, parts: [] }).then((ret) => {
          toast.success("Quotation created !");
          return resolve();
        }).catch((err) => {
          showApiErrorToast(err);
          return reject(err);
        });
      } else {
        const fieldNames = {
          customer: t("common:customer"),
          contact: t("common:contact"),
          technician: t("common:technician"),
          duration: t("common:duration"),
          date: t("common:date:label"),
          pricing: t("common:pricing")
        };

        const errorMessage = validator.getFormErrorMessage(data, QuotationFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };

  const headerActions = (
    <>
      <ExportButton<EasyTech.Quotation> filters={filters} entityName="quotations"
        fileName="quotations.xlsx"
        descriptor={quotationExportDescriptor} getQueryFromFilter={getQueriesFromFilter}
        order={searchOrder}
      />
      <EditCreateDrawer<EasyTech.Quotation>
        entityName={t("quotation:title")}
        FormComponent={QuotationForm}
        onSubmit={handleQuotationCreate}
        isNotEditable={true}
        isNotCreatable={true} /* TMP */
      />
    </>
  );

  const filterDrawerComponent = (
    <FilterDrawer<Partial<EasyTech.Quotation>>
      FormComponent={QuotationFilterForm}
      onSubmit={(data) => {
        // TODO: Refacto - Cette logique particulière de validation de date de RangeDatePicker a été ajoutée ici pour pallier le fait que le form est soumis depuis le bouton du Parent (FilterDrawer) sans déclencher la validation du formulaire de manière native.
        if (data.startDate && !data.endDate) return Promise.reject();
        if (data.endDate && !data.startDate) return Promise.reject();
        return handleFilterSubmit(data);
      }}
      entityName="Quotations"
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={displayFilters}
      onClearFilter={(field, newValue) => {
        // Quand on supprime le badge dateRange, on supprime startDate et endDate
        if (field === "dateRange") {
          handleFilterClear("startDate", null);
          handleFilterClear("endDate", null);
          return;
        }
        handleFilterClear(field, newValue);
      }}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
      entity="quotation"
    />
  );

  const mainContent = quotationsPageState && quotationsPageState.length > 0 ? (
    <QuotationsTable quotations={quotationsPageState} onHeadClick={onOrderChange}
      selectedHead={orderState.head} orderDesc={orderState.orderDesc} />
  ) : (
    <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
      <p className="text-lg text-blue-gray-700">
        {t("quotation:noData")}
      </p>
      <p className="text-sm text-blue-gray-500 mt-2">
        {filters && Object.keys(filters).length > 0
          ? t("quotation:noData.filters")
          : t("quotation:noData.add")}
      </p>
    </div>
  );

  const paginationComponent = quotationsPageState && quotationsPageState.length > 0 ? (
    <Pagination
      actualPage={pageState}
      pageNumber={pageNumberState}
      changePage={setPageState}
    />
  ) : null;

  return (
    <GenericListLayout
      entityName={t("quotation:title")}
      path="/quotations"
      isLoading={isLoading}
      headerActions={headerActions}
      filterDrawer={filterDrawerComponent}
      filterBadges={filterBadgesComponent}
      content={mainContent}
      pagination={paginationComponent}
      className="quotations"
      showBreadcrumb={true}
    />
  );
}
/***/
